import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Eye, Loader2, X, ExternalLink } from 'lucide-react';
import { StickerDoc } from './ui/stickers';
import { viewportOnce } from '../lib/motion';

const RESUME_URL = '/tushar_anand_resume.pdf';

const Resume = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    const link = document.createElement('a');
    link.href = RESUME_URL;
    link.download = 'Tushar_Anand_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsDownloading(false);
  };

  useEffect(() => {
    if (!isPreviewOpen) return;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => e.key === 'Escape' && setIsPreviewOpen(false);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isPreviewOpen]);

  return (
    <section id="resume" className="py-20 md:py-28 bg-cream dark:bg-card/40 border-t-[3px] border-b-[3px] border-ink dark:border-cream">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={viewportOnce}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl bg-paper dark:bg-noir p-8 sm:p-12 brutal-border brutal-shadow-lg flex flex-col md:flex-row items-center md:items-start gap-8 text-center md:text-left"
        >
          <div className="flex-shrink-0 flex h-24 w-24 items-center justify-center rounded-2xl bg-sun brutal-border -rotate-3">
            <StickerDoc className="w-12 h-12 text-ink" />
          </div>

          <div className="flex-1">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-ink dark:text-cream mb-3">
              Want the full story on paper?
            </h2>
            <p className="text-ink/70 dark:text-cream/70 leading-relaxed mb-6 max-w-lg mx-auto md:mx-0">
              Grab my resume for the complete rundown of my experience, skills, and education:
              the same facts as this page, in a format recruiters can file away.
            </p>

            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ink dark:bg-cream text-cream dark:text-ink font-display font-bold brutal-border brutal-hover disabled:opacity-60"
              >
                {isDownloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                {isDownloading ? 'Downloading...' : 'Download PDF'}
              </button>
              <button
                onClick={() => setIsPreviewOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-transparent text-ink dark:text-cream font-display font-bold brutal-border brutal-hover"
              >
                <Eye className="w-5 h-5" />
                Preview
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {isPreviewOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-ink/70 backdrop-blur-sm"
            onClick={() => setIsPreviewOpen(false)}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Resume preview"
              className="flex flex-col w-full h-full sm:h-[88vh] sm:max-w-3xl rounded-2xl overflow-hidden bg-paper dark:bg-noir brutal-border brutal-shadow-lg"
            >
              <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b-[3px] border-ink dark:border-cream bg-cream dark:bg-card flex-shrink-0">
                <span className="font-display font-extrabold text-ink dark:text-cream truncate">
                  Tushar_Anand_Resume.pdf
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={RESUME_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-paper dark:bg-noir brutal-border-thin brutal-hover"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4 text-ink dark:text-cream" />
                  </a>
                  <button
                    onClick={() => setIsPreviewOpen(false)}
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-sun brutal-border-thin brutal-hover"
                    aria-label="Close preview"
                  >
                    <X className="w-4 h-4 text-ink" />
                  </button>
                </div>
              </div>

              <object
                data={`${RESUME_URL}#view=FitH`}
                type="application/pdf"
                className="flex-1 w-full bg-white"
                style={{ minHeight: '60vh' }}
                aria-label="Resume preview"
              >
                {/* Shown when the browser can't render a PDF inline (mobile Safari, etc.) */}
                <div className="flex flex-col items-center justify-center gap-4 h-full min-h-[60vh] p-8 text-center">
                  <p className="text-ink/70">
                    Your browser can't preview PDFs inline.
                  </p>
                  <a
                    href={RESUME_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sun text-ink font-display font-bold brutal-border-thin brutal-hover"
                  >
                    Open resume in a new tab
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </object>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Resume;
